/**
 * The wire shapes `Rws.AiInsights.Host.Reporting.ReportingEndpoints` returns (phase 12, `AIA-81`),
 * and the typed calls to them — following `api/session.ts`'s own pattern exactly: plain `fetch`,
 * `credentials: "same-origin"`, and a discriminated-union result instead of throwing, so a caller
 * cannot forget to check `kind` before reading a field only meaningful for one outcome.
 */
import { apiFetch } from "./apiFetch";

/** One vendor's contribution to a consumption or history response. */
export interface VendorConsumptionResponse {
  vendorId: string;
  displayName: string;
  deepLinkUrl: string;
  hasReading: boolean;
  costAmount: number | null;
  costCurrency: string | null;
  costWasConverted: boolean;
  tokens: number | null;
  requests: number | null;
  latestObservedAt: string | null;
  isIncomplete: boolean;
  nextExpectedRefreshAt: string | null;
  burnRatePerDay: number | null;
  burnRateBasisDays: number | null;
  projectedExhaustionDate: string | null;
  /** Projected spend on each remaining weekday (weekday/weekend rates over the last 7 complete days). */
  weekdayRatePerDay?: number | null;
  /** Projected spend on each remaining Saturday/Sunday. */
  weekendRatePerDay?: number | null;
  /** The part of `costAmount` that is an estimate (Bedrock invocation logs, days CUR does not cover yet); 0 when none, absent for other vendors. */
  estimatedCostAmount?: number | null;
  /** The latest day with a vendor-confirmed cost (for Bedrock, the date CUR last covered). */
  costConfirmedThrough?: string | null;
  /** The part of `costAmount` that is seat fees charged this period; the daily spend and the rates are usage alone. */
  seatFeeAmount?: number | null;
}

/** The one consolidated figure for the current period. */
export interface ConsolidatedFigureResponse {
  amount: number;
  currency: string;
  isFullyConverted: boolean;
  burnRateBasisDays: number;
  burnRatePerDay: number;
  allowanceRemaining: number | null;
  allowancePartiallyConsolidated: boolean;
  projectedExhaustionDate: string | null;
  /** Consolidated projected spend on each remaining weekday. */
  weekdayRatePerDay?: number;
  /** Consolidated projected spend on each remaining Saturday/Sunday. */
  weekendRatePerDay?: number;
  /** The part of `amount` that is seat fees charged this period; the daily spend and the rates are usage alone. */
  seatFeeAmount?: number;
}

/** One vendor's allowance, with the seat-versus-consumption split (`CON-15`). */
export interface AllowanceLineResponse {
  vendorId: string;
  allowanceAmount: number;
  allowanceCurrency: string;
  seatCostAmount: number | null;
  seatCostCurrency: string | null;
  seatCostSource: "administrator-configured" | "default-configuration" | "not-configured";
  consumptionSoFar: number;
  remainingAfterSeat: number | null;
  /** True for a flat-fee provider: allowanceAmount is the fee and there is no limit to track. */
  isFlatFee?: boolean;
}

/** The single most urgent unacknowledged threshold crossing (`PD-24`), or absent. */
export interface ThresholdBannerResponse {
  thresholdPercent: number;
  vendorId: string;
  percentUsed: number;
}

/** The body of `GET /api/reporting/me/consumption`. */
export interface MyConsumptionResponse {
  personId: string;
  periodStart: string;
  periodEnd: string;
  generatedAt: string;
  hasAnyData: boolean;
  firstFiguresExpectedAt: string | null;
  consolidated: ConsolidatedFigureResponse | null;
  vendors: VendorConsumptionResponse[];
  allowances: AllowanceLineResponse[];
  isIncomplete: boolean;
  incompleteVendorIds: string[];
  pendingThresholdBanner: ThresholdBannerResponse | null;
  dailySpend?: DailySpendDayResponse[] | null;
}

/** One vendor's actual spend on a single day from `GET /api/reporting/me/daily-spend`. */
interface DailySpendVendorResponse {
  vendorId: string;
  amount: number;
  currency: string;
}

/** One day in the actual daily spend series from `GET /api/reporting/me/daily-spend`. */
export interface DailySpendDayResponse {
  date: string;
  totalAmount: number;
  currency: string;
  vendors: DailySpendVendorResponse[];
}

/** The body of `GET /api/reporting/me/daily-spend`. */
export interface MyDailySpendResponse {
  personId: string;
  from: string;
  to: string;
  days: DailySpendDayResponse[];
}

/**
 * The outcome of asking for a person's own consumption or history. `denied` is a `403` from the
 * permission-enforcement middleware — an eligible caller missing `consumption.read.own` — and is
 * modelled distinctly from `error` so the UI can show `PermissionDeniedState` rather than a generic
 * failure (the brief's "denied ... never render an empty view").
 */
export type ConsumptionQueryResult<TResponse> =
  | { kind: "ok"; response: TResponse }
  | { kind: "denied" }
  | { kind: "error"; message: string }
  | { kind: "aborted" };

const myConsumptionPath = "/api/reporting/me/consumption";
const myHistoryPath = "/api/reporting/me/history";
const thresholdAcknowledgementsPath = "/api/reporting/me/threshold-acknowledgements";

/**
 * Calls `GET /api/reporting/me/consumption` and interprets the result.
 *
 * @param periodStart - The first day of the reporting period (`YYYY-MM-DD`), or omitted for the current period.
 * @param signal - Aborts the request when the caller no longer wants the response.
 * @returns The consumption for the requested period, a permission denial, an error, or `aborted`.
 */
export async function fetchMyConsumption(
  periodStart?: string,
  signal?: AbortSignal,
): Promise<ConsumptionQueryResult<MyConsumptionResponse>> {
  const path = periodStart ? `${myConsumptionPath}?periodStart=${encodeURIComponent(periodStart)}` : myConsumptionPath;
  return fetchReportingJson<MyConsumptionResponse>(path, "consumption", signal);
}

/** One previous reporting period from `GET /api/reporting/me/history`; its vendor figures are actual totals, with no projection. */
export interface HistoryPeriodResponse {
  periodStart: string;
  periodEnd: string;
  /** The consolidated spend for the period, or null when nothing was convertible. */
  totalAmount: number | null;
  currency: string | null;
  hasAnyData: boolean;
  /** Each vendor's total for the period, in `costAmount`/`costCurrency`. */
  vendors: VendorConsumptionResponse[];
}

/** The body of `GET /api/reporting/me/history`: every previous period with any reading, most recent first, excluding the current period. */
export interface MyHistoryResponse {
  personId: string;
  periods: HistoryPeriodResponse[];
}

/**
 * The body of `GET /api/reporting/users/{personId}/usage`: what someone else sees on their own My Usage page, for a manager
 * who owns a group they are in.
 */
export interface PersonUsageResponse {
  personId: string;
  email: string;
  /** Their consumption for the month, as `/api/reporting/me/consumption` returns it to them. */
  consumption: MyConsumptionResponse;
  /** Their effective allowance this month, in US dollars. */
  effectiveAllowanceUsd: number;
  /** Their earlier months, as `/api/reporting/me/history` returns them to them. */
  history: MyHistoryResponse;
}

/**
 * Calls `GET /api/reporting/users/{personId}/usage`. A person the caller may not see answers 404, so their existence is not
 * revealed; that is reported as `denied`, the same as a refusal.
 *
 * @param personId - The person whose usage is wanted.
 * @param periodStart - Any day in the wanted month; the current month when omitted.
 * @param signal - Aborts the request when the caller no longer wants the response.
 * @returns Their usage, a denial, an error, or `aborted`.
 */
export async function fetchPersonUsage(personId: string, periodStart?: string, signal?: AbortSignal): Promise<ConsumptionQueryResult<PersonUsageResponse>> {
  const base = `/api/reporting/users/${encodeURIComponent(personId)}/usage`;
  const path = periodStart ? `${base}?periodStart=${encodeURIComponent(periodStart)}` : base;
  return fetchReportingJson<PersonUsageResponse>(path, "person usage", signal, true);
}

/**
 * Calls `GET /api/reporting/me/history` and interprets the result.
 *
 * @param signal - Aborts the request when the caller no longer wants the response.
 * @returns The previous periods, a permission denial, an error, or `aborted`.
 */
export async function fetchMyHistory(signal?: AbortSignal): Promise<ConsumptionQueryResult<MyHistoryResponse>> {
  return fetchReportingJson<MyHistoryResponse>(myHistoryPath, "history", signal);
}

/**
 * Dismisses the threshold banner for one threshold, for the current period.
 *
 * @param thresholdPercent - Which of the fixed 50/75/90/100 thresholds to acknowledge.
 * @returns Whether the acknowledgement succeeded.
 */
export async function acknowledgeThreshold(thresholdPercent: number): Promise<ConsumptionQueryResult<null>> {
  try {
    const response = await apiFetch(thresholdAcknowledgementsPath, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ thresholdPercent }),
      cache: "no-store",
    });

    if (response.status === 403) {
      return { kind: "denied" };
    }

    if (!response.ok) {
      return { kind: "error", message: `The threshold-acknowledgement endpoint returned ${String(response.status)}.` };
    }

    return { kind: "ok", response: null };
  } catch {
    return { kind: "error", message: "The threshold-acknowledgement endpoint could not be reached." };
  }
}

async function fetchReportingJson<TResponse>(
  path: string,
  label: string,
  signal?: AbortSignal,
  notFoundIsDenied = false,
): Promise<ConsumptionQueryResult<TResponse>> {
  try {
    const response = await apiFetch(path, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      cache: "no-store",
      signal,
    });

    if (response.status === 403 || (notFoundIsDenied && response.status === 404)) {
      return { kind: "denied" };
    }

    if (!response.ok) {
      return { kind: "error", message: `The ${label} endpoint returned ${String(response.status)}.` };
    }

    const body = (await response.json()) as TResponse;
    return { kind: "ok", response: body };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      return { kind: "aborted" };
    }

    return { kind: "error", message: `The ${label} endpoint could not be reached.` };
  }
}
